<template>
  <form v-show="canAddPayment" ref="form" class="needs-validation" @submit.prevent="save">
    <div v-if="title" class="row">
      <div class="col-sm-12 mb-4">
        <h2>{{ title }}</h2>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-3 px-2">
        <div class="form-group">
          <label class="control-label"> Tipo de Concepto de Pago </label>

          <el-select
            ref="selectedConceptType"
            v-model="selectedConceptType"
            v-validate="modelValidations.conceptPaymentType"
            value-key="id"
            size="small"
            effect="dark"
            name="conceptPaymentType"
            autocomplete="off"
            placeholder="Tipo de Concepto de Pago"
          >
            <el-option
              v-for="option in conceptPaymentTypes"
              :key="option.id"
              :label="option.description"
              :value="option.id"
            >
              <span>{{ option.description }}</span>
            </el-option>
          </el-select>

          <small v-show="conceptPaymentType.invalid" class="text-danger">
            {{ getError('conceptPaymentType') }}
          </small>
        </div>
      </div>

      <!-- disabled for the moment -->
      <div v-if="false" class="col-sm-3 px-2">
        <div class="form-group">
          <label class="control-label"> Importe en Dólares </label>

          <el-input
            v-model="formData.amountInDollars"
            v-validate="modelValidations.amountInDollars"
            name="amountInDollars"
            placeholder="Importe"
          />

          <div v-if="currentDebtInDollars">
            <small class="d-block" :class="debtCanceledClass">
              Deuda: {{ currentDebtInDollars | toCurrencyUSD }}
            </small>
          </div>

          <small v-show="amountInDollars.invalid" class="text-danger">
            {{ getError('amountInDollars') }}
          </small>
        </div>
      </div>

      <div class="col-sm-3 px-2">
        <div class="form-group">
          <label class="control-label"> Importe en Pesos </label>

          <el-input
            v-model="formData.amountInPesos"
            v-validate="modelValidations.amountInPesos"
            name="amountInPesos"
            placeholder="Importe"
          />

          <div>
            <small class="d-block" :class="debtCanceledClass">
              Deuda en pesos: {{ updatedDebtInPesos | toCurrencyARS }}
            </small>
          </div>

          <small v-show="amountInPesos.invalid" class="text-danger">
            {{ getError('amountInPesos') }}
          </small>
        </div>
      </div>

      <div class="col-sm-3 px-2">
        <div class="form-group">
          <label class="control-label"> Número de concepto de pago </label>

          <el-input
            v-model="formData.number"
            v-validate="modelValidations.number"
            name="number"
            pattern="[0-9]+"
            placeholder="Número"
            type="text"
          />

          <small v-show="number.invalid" class="text-danger">
            {{ getError('number') }}
          </small>
        </div>
      </div>

      <div class="col-sm-3 px-2">
        <div class="form-group">
          <label class="control-label"> Banco </label>

          <el-input
            v-model="formData.bank"
            v-validate="modelValidations.bank"
            name="bank"
            placeholder="Banco"
            type="text"
          />

          <small v-show="bank.invalid" class="text-danger">
            {{ getError('bank') }}
          </small>
        </div>
      </div>
      <div class="col-sm-3 px-2">
        <div class="form-group">
          <label class="control-label"> Código Postal </label>

          <el-input
            v-model="formData.postalCode"
            v-validate="modelValidations.postalCode"
            name="postalCode"
            placeholder="Código Postal"
            type="text"
          />

          <small v-show="postalCode.invalid" class="text-danger">
            {{ getError('postalCode') }}
          </small>
        </div>
      </div>

      <div class="col-sm-3 px-2">
        <div class="form-group">
          <label class="control-label"> Fecha Acreditación </label>

          <el-date-picker
            v-model="formData.accreditationDate"
            v-validate="modelValidations.accreditationDate"
            align="center"
            class="d-block"
            format="dd/MM/yyyy"
            name="accreditationDate"
            placeholder="Fecha Acreditación"
            suffix-icon="fas fa-calendar-alt"
            type="date"
            value-format="yyyy-MM-dd"
          />

          <small v-show="accreditationDate.invalid" class="text-danger">
            {{ getError('accreditationDate') }}
          </small>
        </div>
      </div>

      <div class="col-sm-2 text-left pt-4">
        <base-button :disabled="isExceeded" class="rounded-pill mt-2" native-type="submit" size="sm" type="success">
          <i class="fas fa-plus-circle mr-1"></i><span>Agregar</span>
        </base-button>
      </div>
    </div>
  </form>
</template>

<script>
import { Select, Option, DatePicker } from 'element-ui';
import { mapFields } from 'vee-validate';

import ConceptPaymentTypesMixin from '@/mixins/ConceptPaymentTypesMixin';
import { divide, isLessThanZero, subtract } from '@/util/math';
import { CHECK, E_CHECK } from '@/util/constants/conceptPaymentTypes.js';

export default {
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
  },

  mixins: [ConceptPaymentTypesMixin],

  props: {
    title: {
      type: String,
      default: 'Agregar Conceptos de pago',
    },

    receipt: {
      type: Object,
      default: null,
    },

    updatedDebtInDollars: {
      type: Number,
      default: 0,
    },

    updatedDebtInPesos: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      formData: this.getInitialFormData(),
      selectedConceptType: null,
      validated: false,

      modelValidations: {
        conceptPaymentType: {
          required: true,
        },

        amountInDollars: {
          required: true,
          decimal: 2,
        },

        amountInPesos: {
          required: true,
          decimal: 2,
        },

        number: `required_if:selectedConceptType,${CHECK},${E_CHECK}|numeric`,

        bank: `required_if:selectedConceptType,${CHECK},${E_CHECK}|alpha_num`,

        postalCode: `required_if:selectedConceptType,${CHECK},${E_CHECK}|alpha_num`,

        accreditationDate: {
          required: true,
        },
      },
    };
  },

  computed: {
    ...mapFields([
      'conceptPaymentType',
      'amountInDollars',
      'amountInPesos',
      'number',
      'bank',
      'postalCode',
      'accreditationDate',
    ]),

    canAddPayment() {
      return this.updatedDebtInDollars > 0;
    },

    debtCanceledClass() {
      return { 'text-danger': this.isExceeded, 'text-primary': !this.isExceeded };
    },

    isExceeded() {
      return isLessThanZero(this.updatedDebtInPesos);
    },

    currentDebtInDollars() {
      return subtract(this.updatedDebtInDollars, this.formData.amountInDollars);
    },
  },

  watch: {
    'formData.amountInPesos': {
      handler() {
        this.formData.amountInDollars = divide(this.formData.amountInPesos, this.receipt.dollarQuotation);
      },
    },

    selectedConceptType: {
      handler(value) {
        this.formData.conceptPaymentType = this.conceptPaymentTypes.find((type) => type.id === value);
      },
    },
  },

  methods: {
    getInitialFormData() {
      return {
        selectedConceptType: null,
        amountInDollars: null,
        amountInPesos: null,
        number: null,
        bank: '',
        postalCode: '',
        accreditationDate: null,
      };
    },

    async firstFormSubmit() {
      try {
        return await this.$validator.validateAll();
      } finally {
        this.validated = true;
      }
    },

    save() {
      this.firstFormSubmit().then((valid) => {
        if (valid) {
          this.$emit('addItem', this.formData);
          this.clean();
        }
      });
    },

    clean() {
      this.formData = this.getInitialFormData();
      this.selectedConceptType = null;
      this.$validator.reset();
    },

    getError(name) {
      return this.errors.first(name);
    },

    isValid(name) {
      return this.validated && !this.errors.has(name);
    },
  },
};
</script>
